// Imports for external libraries go here.
import clsx from 'clsx';
import { FC, memo, useEffect, useRef } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Container } from '@adobe/aem-react-editable-components';
import {
  setupLinkInterception,
  dispatchCustomEvent,
  WEB_PROMPT_CONSUMED_KEY,
  ACCOUNT_CONTAINER_CENTRALIZED,
} from '@marriott/shared/mi-helper-utils';
import {
  useClientEnvVarsStore,
  useGlobalStore,
  useUserDetailsGlobalStore,
  getUserInfo,
} from '@marriott/mi-store-utils';
import { canUseDOM, Icon } from '@marriott/mi-ui-library';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { clearSessionTimeout, initializeSessionTimeout } from '../../../utils/sessionTimeoutHelper';
import { SignInDrawerWrapper } from '../../SignInDrawer/SignInDrawerWrapper';
import { LanguageSelectorWrapper } from '../LanguageSelector/LanguageSelectorWrapper';
import { MyTrips } from '../MyTrips';
import { UtilityLink } from '../UtilityLink';
import { StyledNavigationUtilityLink } from './NavigationUtilityLink.styles';
import { NavigationUtilityLinkProps, RestPropType, UtilityChildModel } from './NavigationUtilityLink.types';
import {
  getIconClass,
  isLongTextLocale,
  enableLoggedInPanel,
  getCustomerPhoneAndConsentDetails,
  hasFlyoutContainerById,
  hasCentralizedFlyoutContainer,
  findFlyoutEntry,
} from './helper';

import { useNavContext } from '../../../organisms/NavigationContainer';
import {
  ACCOUNT_AUTH_FLYOUT_MODAL_ID,
  ACCOUNT_FLYOUT_MODAL_ID,
  SIGN_IN_LINK_SUFFIX,
  PUBLIC_PAGES,
} from '../../../utils/constants';

const toUtilityChildModel = (itemRecord: Record<string, unknown>): UtilityChildModel => {
  return {
    ...itemRecord,
    cqItems: itemRecord[':items'],
    cqItemsOrder: itemRecord[':itemsOrder'],
  } as UtilityChildModel;
};

// Memoized container for the account flyout datapagecomponent.
// Prevents header re-renders (scroll, hover, sticky) from cascading into
// the FlyoutDrawer tree, which causes mobile keyboard flickering.
const DataPageContainer = memo<{
  utilityNavigationItem: Record<string, unknown>;
  cqPath: string | undefined;
  itemKey: string;
  isAuthorMode: boolean;
}>(({ utilityNavigationItem, cqPath, itemKey, isAuthorMode }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ':type': _type, ...restProps } = utilityNavigationItem;
  const utilityItemPath = `${cqPath}/${itemKey}`;
  const childItemKey = (restProps as Record<string, unknown>)?.[':itemsOrder'] as string[] | undefined;
  const items = (restProps as Record<string, Record<string, unknown>>)?.[':items'];
  const dataPageContainer = items?.['datapagecomponent'] as Record<string, unknown> | undefined;
  const nestedCandidateItems = dataPageContainer?.[':items'] as Record<string, Record<string, unknown>> | undefined;
  const rootCandidateItems = items as Record<string, Record<string, unknown>> | undefined;
  const isSignedInUser = useUserDetailsGlobalStore(state => state.isSignedInUser);
  const modalType = isSignedInUser ? ACCOUNT_AUTH_FLYOUT_MODAL_ID : ACCOUNT_FLYOUT_MODAL_ID;

  const primaryMatch =
    findFlyoutEntry(nestedCandidateItems, modalType) ?? findFlyoutEntry(rootCandidateItems, modalType);
  const centralizedMatch =
    findFlyoutEntry(nestedCandidateItems, ACCOUNT_CONTAINER_CENTRALIZED) ??
    findFlyoutEntry(rootCandidateItems, ACCOUNT_CONTAINER_CENTRALIZED);

  const flyoutKey = primaryMatch?.[0];
  const childModel = primaryMatch?.[1] ? toUtilityChildModel(primaryMatch[1]) : undefined;
  const centralizedFlyoutKey = centralizedMatch?.[0];
  const centralizedChildModel = centralizedMatch?.[1] ? toUtilityChildModel(centralizedMatch[1]) : undefined;

  const fallbackEntry = !childModel ? Object.entries(nestedCandidateItems ?? rootCandidateItems ?? {})[0] : undefined;
  const fallbackFlyoutKey = fallbackEntry?.[0];
  const fallbackChildModel = fallbackEntry ? toUtilityChildModel(fallbackEntry[1]) : undefined;

  const resolvedFlyoutKey = flyoutKey ?? fallbackFlyoutKey;
  const resolvedChildModel = childModel ?? fallbackChildModel;

  const modelCqItems: Record<string, UtilityChildModel> = {};
  const modelCqItemsOrder: string[] = [];

  if (resolvedChildModel && resolvedFlyoutKey) {
    modelCqItems[resolvedFlyoutKey] = resolvedChildModel;
    modelCqItemsOrder.push(resolvedFlyoutKey);
  }

  if (centralizedChildModel && centralizedFlyoutKey) {
    const centralizedKey =
      flyoutKey && centralizedFlyoutKey === flyoutKey ? `${centralizedFlyoutKey}__centralized` : centralizedFlyoutKey;
    modelCqItems[centralizedKey] = centralizedChildModel;
    modelCqItemsOrder.push(centralizedKey);
  }

  const containerProps = {
    cqPath: utilityItemPath,
    cqItemsOrder: childItemKey,
    cqType: 'mi-aem-account/components/content/datapagecomponent',
    model: {
      ':type': 'mi-aem-account/components/content/datapagecomponent',
      id: itemKey || 'datapagecomponent',
      ':path': utilityItemPath,
      cqItems: modelCqItems,
      cqType: 'mi-aem-account/components/content/datapagecomponent',
      cqItemsOrder: modelCqItemsOrder,
    },
  };

  return <Container {...containerProps} model={containerProps?.model} isInEditor={!!isAuthorMode} isPage={false} />;
});

export const NavigationUtilityLink: FC<NavigationUtilityLinkProps> = props => {
  const {
    utilityNavigation,
    className,
    isRenderedInsideOverlay,
    logoUrl = '',
    logoImageAltText = '',
    logoType = '',
    cqPath,
    isAuthorMode,
    isBrandedVariation,
    showBonvoyLogo,
    isSimplifiedNavigation = false,
    featureFlagLegacyNav,
    featureFlagSessionTimeout,
    isWhiteHeader,
    isTransparentHeader,
  } = props;
  const { ':itemsOrder': itemsOrder, ':items': items } = utilityNavigation;
  const { envVarsObject } = useClientEnvVarsStore();
  const locale = envVarsObject?.['ACCEPT_LANGUAGE'];
  const shouldOmitUtilityLabels = isLongTextLocale(locale) || isBrandedVariation;
  const shouldShowBonvoyLogo = showBonvoyLogo && isBrandedVariation;
  const isLegacyNavFlagEnabled = featureFlagLegacyNav?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LEGACY_NAV];
  const { setIsSignInDrawerOpen, setIsAccountLoggedInPanelEnabled } = useNavContext();

  const navigationLinkResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/navigation-link'
    : 'mi-aem-common-spa/components/content/globalnav/navigation-link';

  const languageSelectorResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/language-selector'
    : 'mi-aem-common-spa/components/content/globalnav/language-selector';

  const myTripsResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/mytrips'
    : 'mi-aem-common-spa/components/content/globalnav/mytrips';

  const signInResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/signin'
    : 'mi-aem-common-spa/components/content/globalnav/signin';

  const homeResourceType = 'mi-aem-common-spa/components/content/globalnav/home';

  const isRememberedUser = useUserDetailsGlobalStore(state => state.isRememberMe);
  const isSignedInUser = useUserDetailsGlobalStore.getState().isSignedInUser;
  const myTripsCount = useUserDetailsGlobalStore(state => state?.trips?.upcomingReservationsCount) || 0;
  const isAccountFlyoutEnabled = utilityNavigation?.resourceProperties?.['enableAccountFlyout'] === 'true';
  const hasTriggeredFlyoutOnLoadRef = useRef(false);

  useEffect(() => {
    const triggerFlyoutOnLoad = async () => {
      const urlParams = new URLSearchParams(window?.location?.search);
      const modalId = urlParams.get('modalId');
      const pathname = window?.location?.pathname;
      if (!modalId || hasTriggeredFlyoutOnLoadRef.current) return;
      hasTriggeredFlyoutOnLoadRef.current = true;

      const hasWebPromptBeenConsumed = sessionStorage.getItem(WEB_PROMPT_CONSUMED_KEY) === 'true';

      if (modalId === 'webPrompt' && hasWebPromptBeenConsumed) return;

      // Wait for user details to be fetched (reuses in-flight request if one exists)
      try {
        await getUserInfo(true);
        // eslint-disable-next-line no-empty
      } catch {
        // If fetching user info fails, we still want to proceed with the flyout trigger logic using the existing user state.
      }
      const isUserSignedIn = useUserDetailsGlobalStore.getState().isSignedInUser;

      const isDefaultPage = pathname?.endsWith('/default.mi');
      const isPublicPage = PUBLIC_PAGES.some(page => pathname?.endsWith(page));
      const hasCentralizedAccountFlyout = hasCentralizedFlyoutContainer(items?.['utilityItem_5']);
      const hasPrimaryAccountFlyout = hasFlyoutContainerById(items?.['utilityItem_5'], ACCOUNT_FLYOUT_MODAL_ID);
      let hasMobileTypePhone = false;
      if (modalId === 'webPrompt' && isUserSignedIn && !hasWebPromptBeenConsumed) {
        const consumerId = (window?.sessionData?.data?.consumerID ||
          window?.sessionData?.cacheData?.data?.consumerID) as string | undefined;
        const phoneDetailsResponse = await getCustomerPhoneAndConsentDetails(consumerId, locale);
        hasMobileTypePhone =
          phoneDetailsResponse?.customer?.contactInformation?.phones?.some(
            (phone: { type?: { code?: string } }) => phone?.type?.code === 'MT'
          ) ?? false;
      }

      const shouldDispatch =
        modalId === 'logout'
          ? !isUserSignedIn && isDefaultPage && hasPrimaryAccountFlyout
          : modalId === 'sessionTimedOut'
          ? !isUserSignedIn && (isPublicPage || isDefaultPage) && hasPrimaryAccountFlyout
          : modalId === 'signIn'
          ? !isUserSignedIn
          : modalId === 'webPrompt'
          ? isUserSignedIn && !hasWebPromptBeenConsumed && hasCentralizedAccountFlyout && !hasMobileTypePhone
          : true;
      if (shouldDispatch) {
        // Yield a frame so DataPageContainer re-renders with the updated isSignedInUser
        // before the event is dispatched, ensuring the correct flyout container is mounted.
        setIsSignInDrawerOpen(true);
        await new Promise(resolve => requestAnimationFrame(resolve));
        dispatchCustomEvent(false, modalId);
        if (modalId === 'webPrompt' && !hasWebPromptBeenConsumed) {
          sessionStorage.setItem(WEB_PROMPT_CONSUMED_KEY, 'true');
        }
      }
    };

    if (document.readyState === 'complete') {
      triggerFlyoutOnLoad();
      return;
    }

    window.addEventListener('load', triggerFlyoutOnLoad);
    return () => window.removeEventListener('load', triggerFlyoutOnLoad);
  }, [setIsSignInDrawerOpen]);

  useEffect(() => {
    useGlobalStore.setState({ isAccountFlyoutEnabled });
  }, [isAccountFlyoutEnabled]);

  const currentUrl = canUseDOM ? window.location.href : undefined;
  const hasAccountLoggedInPanel = enableLoggedInPanel(items?.['utilityItem_5'], currentUrl);
  useEffect(() => {
    setIsAccountLoggedInPanelEnabled(hasAccountLoggedInPanel);
  }, [hasAccountLoggedInPanel, setIsAccountLoggedInPanelEnabled]);

  const renderUtilityLink = (
    restProps: RestPropType,
    defaultIconClass: string,
    buttonText?: string,
    buttonType?: string
  ) => {
    const { labelTag: iconClassTag, 'cq:panelUrl': href, openInaNewTab, 'cq:panelTitle': linkText } = restProps;

    //TODO: Keeping the fallback class as globe as of now as currentlty no option from AEM to author icon. Later should be changed.
    const iconClass = getIconClass(iconClassTag || defaultIconClass);
    const target = openInaNewTab === 'true' ? '_blank' : '_self';
    const rel = openInaNewTab === 'true' ? 'noopener noreferrer' : undefined;
    return (
      <>
        <Icon
          iconClass={`icon-${iconClass} utility-icon custom_click_track`}
          ariaLabel={restProps['cq:panelTitle']}
          iconHref={href}
          target={target}
          rel={rel}
          enableLinkClsName
          isDecorative={true}
        >
          {href && (!shouldOmitUtilityLabels || buttonType === 'signin') && (
            <span
              aria-hidden="true"
              className={clsx('utility-link-text', {
                'd-none d-xl-inline-block': !isRenderedInsideOverlay,
              })}
            >
              {linkText || buttonText}
            </span>
          )}
        </Icon>
        {!href && (!shouldOmitUtilityLabels || buttonType === 'signin') && (
          <span
            aria-hidden="true"
            className={clsx('utility-link-text', {
              'd-none d-xl-inline-block': !isRenderedInsideOverlay,
              [isBrandedVariation ? 'd-md-inline-block' : 'd-lg-inline-block']: buttonType === 'signin',
            })}
          >
            {linkText || buttonText}
          </span>
        )}
      </>
    );
  };

  const isSessionTimeoutMigrationEnabled =
    featureFlagSessionTimeout?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SESSION_TIMEOUT_MIGRATION];

  useEffect(() => {
    if (isSessionTimeoutMigrationEnabled) {
      return;
    }

    initializeSessionTimeout(utilityNavigation?.resourceProperties?.['sessionTimeOutTimer']);

    return () => {
      clearSessionTimeout();
    };
  }, [isSessionTimeoutMigrationEnabled, utilityNavigation?.resourceProperties]);

  useEffect(() => {
    const { interceptAnchorClicks } = setupLinkInterception(SIGN_IN_LINK_SUFFIX, () => {
      dispatchCustomEvent(!isRememberedUser);
      setIsSignInDrawerOpen(true);
    });
    const cleanupFn = isAccountFlyoutEnabled && interceptAnchorClicks();
    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [isAccountFlyoutEnabled, isRememberedUser, setIsSignInDrawerOpen]);

  return (
    <StyledNavigationUtilityLink
      className={clsx(className, 'pr-3 pr-md-5 pr-lg-0', {
        'align-items-baseline': isAuthorMode,
        'mt-lg-3 mt-md-3': isSimplifiedNavigation && !isRenderedInsideOverlay,
      })}
      isRenderedInsideOverLay={isRenderedInsideOverlay}
      isBrandedVariation={isBrandedVariation}
    >
      {/* this svg should be removed once the logo is put into the design system */}
      {shouldShowBonvoyLogo && (
        <li className={clsx('utility-item pt-1', 'utility-logo-sign-in')}>
          <a href="/default.mi">
            <div className="brand-logo-marriott-bonvoy-wordmark" aria-label="Marriott Bonvoy" />
          </a>
        </li>
      )}
      {itemsOrder.map((key: string) => {
        const utilityNavigationItem = items[key];

        const { ':type': type, ...restProps } = utilityNavigationItem;
        const trackingContentPosition = items?.['trackingProperties']?.trackingContentPosition;

        switch (type) {
          case navigationLinkResourceType:
            return (
              <li
                key={restProps['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-link',
                  'utility-link-selector',
                  { 't-label-inverse-xs d-none d-lg-block': !isRenderedInsideOverlay },
                  { 'd-md-block': isBrandedVariation },
                  { 't-label-alt-xs': isRenderedInsideOverlay },
                  { 'color-scheme7': isBrandedVariation && (isWhiteHeader || !isTransparentHeader) }
                )}
              >
                <UtilityLink
                  restProps={{ trackingContentPosition, ...restProps }}
                  defaultIconClass="help"
                  isBrandedVariation={isBrandedVariation}
                  isRenderedInsideOverlay={isRenderedInsideOverlay}
                />
              </li>
            ); //later a seperate component for utility link can be created: <UtilityLink {...restProps} />; //Logic for Links such as help, covid-19

          case languageSelectorResourceType:
            return (
              <li
                key={restProps['cq:panelTitle'] || restProps?.resourceProperties['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-language-selector',
                  { 't-label-inverse-xs d-none d-md-block': !isRenderedInsideOverlay && isBrandedVariation },
                  { 't-label-inverse-xs d-none d-lg-block': !isRenderedInsideOverlay && !isBrandedVariation },
                  { 't-label-alt-xs': isRenderedInsideOverlay }
                )}
              >
                <LanguageSelectorWrapper
                  restProps={{ trackingContentPosition, ...restProps }}
                  renderUtilityLink={renderUtilityLink}
                  buttonClassName={clsx('t-label-inverse-xs', {
                    'color-scheme7': isBrandedVariation && (isWhiteHeader || !isTransparentHeader),
                  })}
                  cqPath={`${cqPath}/${key}`}
                  isAuthorMode={isAuthorMode}
                />
              </li>
            );

          case myTripsResourceType:
            return !isRenderedInsideOverlay ? (
              <li
                key={restProps['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-trips',
                  isBrandedVariation && (isSignedInUser ? 'utility-trips-signed-in' : 'utility-trips-sign-in')
                )}
              >
                <MyTrips
                  trackingContentPosition={trackingContentPosition}
                  restProps={restProps}
                  buttonClassName={clsx({
                    'color-scheme7': isBrandedVariation && (isWhiteHeader || !isTransparentHeader),
                  })}
                >
                  <>
                    {renderUtilityLink(restProps, 'nav---trips')}
                    {isBrandedVariation && isSignedInUser && (
                      <span aria-hidden="true" className="utility-link-text d-none d-md-block d-lg-inline-block">
                        {restProps['cq:panelTitle']}
                        {myTripsCount > 0 && ` (${myTripsCount})`}
                      </span>
                    )}
                  </>
                </MyTrips>
              </li>
            ) : null; //later a seperate component for mytrips can be created: <MyTrips {...restProps} />; //Logic for Trips
          case homeResourceType:
            return !isRenderedInsideOverlay && isBrandedVariation ? (
              <li
                key={restProps['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-link',
                  'utility-home',
                  'd-none d-md-block d-lg-block',
                  { 't-label-inverse-xs': !isRenderedInsideOverlay },
                  { 't-label-alt-xs': isRenderedInsideOverlay },
                  { 'color-scheme7': isBrandedVariation && (isWhiteHeader || !isTransparentHeader) }
                )}
              >
                {renderUtilityLink(restProps, 'nav---home')}
              </li>
            ) : null;

          case signInResourceType:
            return !isRenderedInsideOverlay ? (
              <li
                key={'signinbutton'}
                className={clsx('utility-item', 'utility-sigin', isBrandedVariation && 'utility-signed-in')}
              >
                <SignInDrawerWrapper
                  logoUrl={logoUrl}
                  logoImageAltText={logoImageAltText}
                  logoType={logoType}
                  restProps={restProps}
                  renderUtilityLink={renderUtilityLink}
                  cqPath={`${cqPath}/${key}`}
                  isAuthorMode={isAuthorMode}
                  isBrandedVariation={isBrandedVariation}
                  isAccountFlyout={isAccountFlyoutEnabled}
                  isWhiteHeader={isWhiteHeader}
                  isTransparentHeader={isTransparentHeader}
                />
              </li>
            ) : null;

          // Accept both datapagecomponent and global-header-sign-in variants
          case 'mi-aem-account/components/content/datapagecomponent': {
            // Render the Container if not inside overlay and (account flyout is enabled OR in author mode)
            return !isRenderedInsideOverlay && (isAccountFlyoutEnabled || isAuthorMode) ? (
              <li key={key} id="utility-datapagecomponent" className={clsx('utility-datapagecomponent')}>
                <DataPageContainer
                  utilityNavigationItem={utilityNavigationItem}
                  cqPath={cqPath}
                  itemKey={key}
                  isAuthorMode={!!isAuthorMode}
                />
              </li>
            ) : null;
          }

          default:
            return null;
        }
      })}
    </StyledNavigationUtilityLink>
  );
};
